import React, { ReactNode, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import style from "./dialog.module.scss";
import { Icon, jnjCloseNaked } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { createPortal } from "react-dom";

interface DialogProps {
    children: ReactNode;
    open?: boolean;
    noSnippet?: boolean;
    showClose?: boolean;
    closeDialogTitle?: string;
    className?: string;
    testId?: string;
    onClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

const useScrollVisibility = (elementRef: React.RefObject<HTMLElement>, isMounted: boolean) => {
    const [scrollState, setScrollState] = useState({
        hasVerticalScroll: false,
        hasHorizontalScroll: false,
    });

    useEffect(() => {
        if (!isMounted) return;

        const element = elementRef.current;
        if (!element) return;

        const checkScroll = () => {
            setScrollState({
                hasVerticalScroll: element.scrollHeight > element.clientHeight,
                hasHorizontalScroll: element.scrollWidth > element.clientWidth,
            });
        };

        // Initial check
        checkScroll();

        // Set up observer for dynamic changes
        const observer = new ResizeObserver(checkScroll);
        observer.observe(element);
        return () => observer.disconnect();
    }, [elementRef, isMounted]);

    return scrollState;
};

export const Dialog = ({
    children,
    open = true,
    noSnippet = false,
    showClose = true,
    closeDialogTitle = "Close",
    className,
    testId = "Dialog",
    onClose = () => {},
}: DialogProps) => {
    const closeButtonRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            // Set mounted state after a small delay to ensure portal is rendered
            const timer = setTimeout(() => {
                setIsMounted(true);
            }, 0);
            return () => {
                clearTimeout(timer);
                document.body.style.overflow = "unset";
            };
        } else {
            document.body.style.overflow = "unset";
            setIsMounted(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, [open]);

    const { hasVerticalScroll } = useScrollVisibility(wrapperRef, isMounted);

    if (!open) return null;

    const dialog = createPortal(
        <div
            id="dialogComponent"
            className={classnames(style["dialog-overlay"])}
            data-test-id={testId}
            {...(noSnippet ? { "data-nosnippet": true } : {})}
        >
            <div className={style["dialog-content"]} data-test-id="Dialog.Content">
                <div
                    className={classnames(style["dialog-spacer"], hasVerticalScroll && style["vertical-scroll-start"])}
                ></div>
                <div
                    className={classnames(style.wrapper, className)}
                    data-test-id="Dialog.Wrapper"
                    id="scroll-container"
                    ref={wrapperRef}
                >
                    {onClose && (
                        <button className={style["backdrop-button"]} onClick={onClose} aria-label="Close dialog" />
                    )}
                    {onClose && showClose && (
                        <div
                            ref={closeButtonRef}
                            tabIndex={0}
                            className={style["close-icon"]}
                            onClick={onClose}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    onClose(e);
                                }
                            }}
                            data-test-id="Dialog.Close"
                            title={closeDialogTitle}
                            role="button"
                        >
                            <Icon icon={jnjCloseNaked} type="jnj" color="black" size="medium" />
                        </div>
                    )}
                    {children}
                </div>
                <div
                    className={classnames(style["dialog-spacer"], hasVerticalScroll && style["vertical-scroll-end"])}
                ></div>
            </div>
        </div>,
        document.body,
    );

    return dialog;
};
